import { html, Page, View } from 'rune-ts';
import klass from './ProductList.module.scss';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { footer_links } from '../../components/cells/Footer/constant';
import { Dropdown, DropdownChangeEvent } from '../../components/cells/Dropdown/Dropdown';
import { Pagination, PaginationChangeEvent } from '../../components/cells/Pagination/Pagination';
import { UtilF } from '../../../../../modules/Util/F/Function/module/UtilF';
import { PaginationData } from '../../../features/Pagination/type';
import { ProductFilterModal } from '../../components/cells/ProductFilterModal/ProductFilterModal';
import { classes, htmlIf, parseQuery, updateUrlParams } from '../../../shared/util';
import { loading, loadingEnd } from '../../../shared/client/global';
import { ProductListApiS } from '../../../features/ProductList/outbound/share';
import {
  ProductFilterChangedEvent,
  ProductFilterConfirmEvent,
} from '../../components/cells/ProductFilterModal/views/ProductFilterBody';
import { entries, isNumber, join, map, pipe, toArray } from '@fxts/core';
import {
  CreatorFilter,
  DefaultProductListSort,
  ProductCardViewData,
  ProductFilterOption,
  StyleFilter,
} from '../../../features/ProductList/type';
import {
  PRODUCT_LIST_SORT,
  ProductListSortOption,
  ProductListSortOptionMap,
} from '../../../features/ProductList/constant';
import { ProductListCount } from './view/ProductListCount';
import {
  ButtonRemovedEvent,
  ProductFilterResetEvent,
  ProductFilterToggleButton,
} from './view/ProductFilterToggleButton';
import { listenDocumentEvent, sendDocumentEvent } from '../../../shared/util/event';
import { BaseLayoutOptions } from '../../../features/Core/service';
import { ButtonCheckbox } from '../../components/atoms/ButtonCheckbox/ButtonCheckbox';
import { FloatingButton_filter } from '../../components/cells/FloatingBox/FloatingButtons/FloatingButton_filter';
import { EXCLUDE_MAKER_MODULE } from '../../../shared/constant';

import { CateRootGnbMoView } from '@services/MarppleShop/renderApp/components/cells/CateRootGnbMo/CateRootGnbMoView.ts';
import { CategoryLevel1_CODE, ICategory } from '../../../../../shared/mps-shared/domain/category/types.ts';
import {
  CateListWithRankingEvent,
  CateListWithRankingView,
} from '@services/MarppleShop/renderApp/components/cells/CateListWithRanking/CateListWithRankingView.ts';
import {
  ProductListCateItemEvent,
  ProductListCateItemView,
} from '@services/MarppleShop/renderApp/pages/ProductList/view/ProductListCateItemView.ts';
import { CardProductControllerView } from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/CardProduct/CardProductControllerView.ts';
import { DefaultProductListEmptyScreen } from '@services/MarppleShop/renderApp/components/cells/ProductCardList/ProductCardListEmptyScreen.ts';

export interface ProductListPageData {
  products: ProductCardViewData[];
  count: number;
  pagination: PaginationData;
  creator_filters: CreatorFilter[];
  style_filters: StyleFilter[];
  init_filter: ProductFilterOption;
  sort: DefaultProductListSort;
  cate_list_id?: number;
  cate_item_id?: number;
  layout_options: BaseLayoutOptions;
  without_sold_out?: boolean;
  is_limited?: boolean;
  cate_root_code: CategoryLevel1_CODE;
  readonly cate_root: ICategory;
  selected_cate_list: ICategory;
  selected_cate_item?: ICategory;
}

class ProductListBodyView extends View<ProductListPageData> {
  ProductFilterModalView: ProductFilterModal;
  PaginationView: Pagination;
  ProductListCountView: ProductListCount;
  ProductFilterToggleButtonView: ProductFilterToggleButton;
  WithoutSoldOutCheckbox: ButtonCheckbox;
  IsLimitedCheckbox: ButtonCheckbox;

  private CateListWithRankingView: CateListWithRankingView = new CateListWithRankingView(
    {
      cate_root: this.data.cate_root,
      cate_root_code: this.data.cate_root_code,
      is_ranking_page: false,
      selected_cate_list_code: this.data.selected_cate_list.code,
      without_sold_out: this.data.without_sold_out,
    },
    {
      is_mobile: this.is_mobile,
    },
  );

  private productListCateItemView = new ProductListCateItemView({ ...this.data, is_mobile: this.is_mobile });

  LIMIT_PER_PAGE: Readonly<number>;
  PAGINATION_SIZE: Readonly<number>;

  PRODUCT_LIST_SORT_MAP: Readonly<ProductListSortOptionMap>;
  PRODUCT_LIST_SORT_OPTIONS: ProductListSortOption[];
  DEFAULT_PRODUCT_LIST_SORT_OPTION: Readonly<ProductListSortOption>;
  SELECTED_PRODUCT_LIST_SORT_OPTION: Readonly<ProductListSortOption>;

  constructor(data: ProductListPageData) {
    super(data);
    this.LIMIT_PER_PAGE = this.is_mobile ? 60 : 100;
    this.PAGINATION_SIZE = this.is_mobile ? 5 : 10;

    this.ProductFilterModalView = new ProductFilterModal(
      { ...this.data, is_mobile: this.is_mobile },
      this.data.init_filter,
    );

    this.PRODUCT_LIST_SORT_MAP = {
      popular: {
        key: PRODUCT_LIST_SORT.popular,
        value: PRODUCT_LIST_SORT.popular,
        name: ET('mps2::product::sort::popular'),
      },
      newest: {
        key: PRODUCT_LIST_SORT.newest,
        value: PRODUCT_LIST_SORT.newest,
        name: ET('mps2::product::sort::newest'),
      },
      price_low_to_high: {
        key: PRODUCT_LIST_SORT.price_low_to_high,
        value: PRODUCT_LIST_SORT.price_low_to_high,
        name: ET('mps2::product::sort::price_low_to_high'),
      },
      price_high_to_low: {
        key: PRODUCT_LIST_SORT.price_high_to_low,
        value: PRODUCT_LIST_SORT.price_high_to_low,
        name: ET('mps2::product::sort::price_high_to_low'),
      },
      many_reviews: {
        key: PRODUCT_LIST_SORT.many_reviews,
        value: PRODUCT_LIST_SORT.many_reviews,
        name: ET('mps2::product::sort::many_reviews'),
      },
      featured: {
        key: PRODUCT_LIST_SORT.featured,
        value: PRODUCT_LIST_SORT.featured,
        name: ET('mps2::product::sort::featured'),
      },
    };
    this.PRODUCT_LIST_SORT_OPTIONS = pipe(
      this.PRODUCT_LIST_SORT_MAP,
      entries,
      map(([_, value]) => value),
      toArray,
    );
    this.DEFAULT_PRODUCT_LIST_SORT_OPTION = this.PRODUCT_LIST_SORT_MAP.popular;
    this.SELECTED_PRODUCT_LIST_SORT_OPTION =
      this.PRODUCT_LIST_SORT_MAP[this.data.sort ?? this.DEFAULT_PRODUCT_LIST_SORT_OPTION.key];

    // 품절 상품 제외
    this.WithoutSoldOutCheckbox = new ButtonCheckbox({
      active: this.data.without_sold_out,
      label: this.is_mobile
        ? ET('mps2::product::filter::without_sold_out_short')
        : ET('mps2::product::filter::without_sold_out'),
      name: 'without_sold_out',
    });

    // 한정판만 보기
    this.IsLimitedCheckbox = new ButtonCheckbox({
      active: this.data.is_limited,
      label: this.is_mobile
        ? ET('mps2::product::filter::is_limited_short')
        : ET('mps2::product::filter::is_limited'),
      name: 'is_limited',
    });

    this.PaginationView = new Pagination(
      {
        page: this.data.pagination.page,
        size: this.PAGINATION_SIZE,
        total: Pagination.calcTotal({ total_count: this.data.count, limit_per_page: this.LIMIT_PER_PAGE }),
      },
      { klass: klass.pagination },
    );

    this.ProductListCountView = new ProductListCount(this.data);

    this.ProductFilterToggleButtonView = new ProductFilterToggleButton(this.data.init_filter, data);
  }

  protected override onRender() {
    // 페이지네이션 변경 이벤트
    this.delegate(PaginationChangeEvent, Pagination, async (e) => {
      history.replaceState(null, '', updateUrlParams(location.href, { page: e.detail.page }));
      await this.refreshProductList();
    });

    // 카테고리 변경 이벤트
    this.delegate(CateListWithRankingEvent, CateListWithRankingView, async (e) => {
      e.detail.originalEvent.preventDefault();
      const selected_cate_list = e.detail.view.getSelectedCateList();
      if (selected_cate_list) {
        this.productListCateItemView.setSelectedCateList(selected_cate_list).redraw();
        history.replaceState(
          null,
          '',
          updateUrlParams(location.href, {
            cate_list_id: e.detail.cate_list_id,
            cate_item_id: void 0,
            page: 1,
          }),
        );
        await this.refreshProductList();
      }
    });

    this.delegate(ProductListCateItemEvent, ProductListCateItemView, async (e) => {
      e.detail.originalEvent.preventDefault();
      history.replaceState(
        null,
        '',
        updateUrlParams(location.href, {
          cate_list_id: e.detail.cate_list_id,
          cate_item_id: e.detail.cate_item_id,
          page: 1,
        }),
      );
      await this.refreshProductList();
    });

    // 정렬 드롭다운 변경 이벤트
    this.delegate(DropdownChangeEvent<keyof typeof PRODUCT_LIST_SORT>, Dropdown, async (e) => {
      const sort = e.detail.option!.value as keyof typeof PRODUCT_LIST_SORT;
      history.replaceState(null, '', updateUrlParams(location.href, { sort, page: 1 }));
      await this.refreshProductList();
    });

    // 상품 필터 모달에서 "상품 보기" 클릭 시
    this.delegate(ProductFilterConfirmEvent, ProductFilterModal, async (e, view) => {
      view.updateFilter(e.detail);
      this.ProductFilterToggleButtonView.setData(e.detail);
      this.ProductFilterToggleButtonView.redraw();
      this.updateUrlParams({
        ...e.detail,
        page: 1,
        is_limited: this.IsLimitedCheckbox.isChecked(),
        without_sold_out: this.WithoutSoldOutCheckbox.isChecked(),
      });

      sendDocumentEvent('floating_bot_button_filter_active', {
        active: this.ProductFilterModalView.FilterBody.isValueSelected(),
      });

      await this.refreshProductList();
    });

    // 상품페이지 하단에 있는 sticky 상품필터 open 이벤트
    this.delegate('click', `.${ProductFilterToggleButton} .${klass.toggle}`, () => {
      this.ProductFilterModalView.open();
    });

    // 상품 필터 모달에서 값 변경 시 이벤트
    this.delegate(ProductFilterChangedEvent, ProductFilterToggleButton, async (e) => {
      this.ProductFilterModalView.updateFilter(e.detail);
      this.updateUrlParams({ ...e.detail, page: 1 });
      await this.refreshProductList();
    });

    // 상품페이지 하단에 있는 sticky 상품필터 "초기화" 시 이벤트
    this.delegate(ProductFilterResetEvent, ProductFilterToggleButton, async (_, view) => {
      const filter_option = this.ProductFilterModalView.clear();
      view.setData(filter_option);
      view.redraw();
      this.flushFilterInUrlParams();
      await this.refreshProductList();
    });

    // 상품페이지 하단에 있는 sticky 상품필터 "단일 삭제" 이벤트
    this.delegate(ButtonRemovedEvent, ProductFilterToggleButton, async (e) => {
      const { id, value } = e.detail;
      const data = this.ProductFilterToggleButtonView.remove(id, value);
      this.ProductFilterToggleButtonView.redraw();
      this.ProductFilterModalView.updateFilter(data);
      this.updateUrlParams({ ...data, page: 1 });
      await this.refreshProductList();
    });

    this.delegate('click', ButtonCheckbox, async (_, view) => {
      history.replaceState(
        null,
        '',
        updateUrlParams(location.href, {
          page: 1,
          [view.data.name]: view.isChecked() ? 'true' : null,
        }),
      );
      await this.refreshProductList();
    });

    // floating box 내의 filter 버튼 클릭 시, 상품필터 open
    listenDocumentEvent('floating_box_button_click', (e) => {
      if (e.detail.name == FloatingButton_filter.toString()) {
        this.ProductFilterModalView.open();
      }
    });
  }

  // 상품 필터 관련된 query string 없애고, page 1 로 초기화
  private flushFilterInUrlParams() {
    this.updateUrlParams({
      min_price: undefined,
      max_price: undefined,
      is_limited: this.IsLimitedCheckbox.isChecked(),
      without_sold_out: this.WithoutSoldOutCheckbox.isChecked(),
      creator_filter_ids: undefined,
      style_filter_ids: undefined,
      page: 1,
    });
  }

  private updateUrlParams(
    option: ProductFilterOption & {
      page?: number;
      without_sold_out?: boolean;
      is_limited?: boolean;
    },
  ) {
    const { min_price, max_price, without_sold_out, is_limited, creator_filter_ids, style_filter_ids, page } =
      option;

    history.replaceState(
      null,
      '',
      updateUrlParams(location.href, {
        min_price,
        max_price,
        without_sold_out: without_sold_out ? 'true' : undefined,
        is_limited: is_limited ? 'true' : undefined,
        creator_filter_ids: joinFilter(creator_filter_ids),
        style_filter_ids: joinFilter(style_filter_ids),
        // 없으면 기존 페이지 유지
        ...(page ? { page } : {}),
      }),
    );
  }

  private get is_mobile() {
    return this.data.layout_options.is_mobile;
  }

  private async setProductsData() {
    const option = parseQuery(location.search);
    const { products, total_count } = await ProductListApiS.getPaginatedProductListApi({
      ...option,
      limit: this.LIMIT_PER_PAGE,
      cate_root_id: this.data.cate_root.id,
    });
    this.data.products = products;
    this.data.count = total_count;
    this.data.pagination = {
      page: isNumber(option.page) ? option.page : 1,
      total: Pagination.calcTotal({ total_count, limit_per_page: this.LIMIT_PER_PAGE }),
      size: this.PAGINATION_SIZE,
    };

    const container = this.element().querySelector(`.${klass.product_list}`);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    container!.classList.toggle(klass.empty, products.length === 0);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    container!.replaceChildren(
      ...(this.data.products.length > 0
        ? this.data.products.map((product) =>
            new CardProductControllerView({
              card_product_view_props: {
                type:
                  this.data.cate_root_code === CategoryLevel1_CODE.GOODS ||
                  this.data.cate_root_code === CategoryLevel1_CODE.HANDMADE
                    ? 'D'
                    : 'F',
                size: this.is_mobile ? 'Medium' : 'Large',
                layout: 'Vertical',
                theme: 'Light',
              },
              product_card_view_data: product,
              options: {
                resize_width: 600,
              },
            }).render(),
          )
        : [new DefaultProductListEmptyScreen().render()]),
    );

    this.PaginationView.setData(this.data.pagination);
    this.ProductListCountView.setCount(this.data.count);
  }

  private async refreshProductList() {
    loading();
    await this.setProductsData();
    this.PaginationView.redraw();
    this.ProductListCountView.redraw();
    UtilF.refreshLazyLoad();
    window.scrollTo({ top: 0 });
    loadingEnd();
  }

  protected override template() {
    const { cate_root_code, products } = this.data;
    return html`
      <div>
        ${this.ProductFilterModalView}
        <div class="${klass.container}">
          <div class="${klass.cate_list_header}">
            ${this.is_mobile ? new CateRootGnbMoView({ cate_root_code }) : ''}
            <div class="${klass.cate_list}">${this.CateListWithRankingView}</div>
            ${this.productListCateItemView}
          </div>
          <div class="${klass.body}">
            <div class="${klass.product_section}">
              <div class="${klass.control_panel}">
                ${htmlIf(this.ProductListCountView, !this.is_mobile)}
                <div class="${klass.filter_container}">
                  ${this.WithoutSoldOutCheckbox}${cate_root_code == CategoryLevel1_CODE.EBOOK
                    ? ''
                    : this.IsLimitedCheckbox}
                </div>
                ${new Dropdown(
                  {
                    options: this.PRODUCT_LIST_SORT_OPTIONS,
                    selected_option_key: this.SELECTED_PRODUCT_LIST_SORT_OPTION.key,
                  },
                  {
                    horizontal: 'right',
                    without_container: this.is_mobile,
                    default_name: this.PRODUCT_LIST_SORT_MAP.price_high_to_low.name,
                    fit_width_to_longest_name: true,
                    text_align: this.is_mobile ? 'right' : 'left',
                  },
                )}
              </div>

              <div
                class="${classes(klass.product_list, {
                  [klass.empty]: products.length === 0,
                })}"
              >
                ${products.length > 0
                  ? products.map(
                      (product) =>
                        new CardProductControllerView({
                          card_product_view_props: {
                            type:
                              cate_root_code === CategoryLevel1_CODE.GOODS ||
                              cate_root_code === CategoryLevel1_CODE.HANDMADE
                                ? 'D'
                                : 'F',
                            size: this.is_mobile ? 'Medium' : 'Large',
                            layout: 'Vertical',
                            theme: 'Light',
                          },
                          product_card_view_data: product,
                          options: {
                            resize_width: 600,
                          },
                        }),
                    )
                  : new DefaultProductListEmptyScreen()}
              </div>

              ${this.PaginationView}
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

export class ProductListPage extends Page<ProductListPageData> {
  protected override template() {
    const { layout_options } = this.data;

    const body = new ProductListBodyView(this.data);

    return html`
      <div class="${EXCLUDE_MAKER_MODULE}">
        ${new LayoutView(
          {
            body,
            footer_data: { ...footer_links, is_mobile: layout_options.is_mobile, lang: ET.lang },
          },
          {
            require_top_arrow_button: true,
            has_bottom_tab_bar: true,
            selected_bottom_tab_idx: -1,
            floating_box: {
              filter: {
                active: body.ProductFilterModalView.FilterBody.isValueSelected(),
              },
            },
            ...layout_options,
          },
        )}
      </div>
    `;
  }
}

const joinFilter = (ids?: number[]) => {
  if (!ids || !ids.length) {
    return undefined;
  }
  return pipe(
    ids,
    map((id) => id.toString()),
    join(','),
  );
};
